const INACTIVITY_CUTOFF_MS = 30_000;

export type EngagementSurface = 'material' | 'teaching' | 'inactive';

export interface EngagementSnapshot {
  materialVisibleMs: number;
  materialActiveMs: number;
  teachingActiveMs: number;
  activeSurface: EngagementSurface;
}

type InteractiveSurface = Exclude<EngagementSurface, 'inactive'>;

/**
 * Pure interval accumulator for workspace engagement.
 *
 * Callers supply one monotonic time domain. A stale or non-finite method time
 * is ignored as a whole operation so malformed input cannot move the clock or
 * retroactively change ownership. Equal times are valid and call order wins.
 */
export class StudyEngagementClock {
  private advancedAt: number;
  private documentVisible = true;
  private materialVisible = true;
  private owner: InteractiveSurface | undefined;
  private ownerInteractionAt: number | undefined;
  private materialVisibleMs = 0;
  private materialActiveMs = 0;
  private teachingActiveMs = 0;

  constructor(startedAt: number) {
    if (!Number.isFinite(startedAt)) {
      throw new RangeError('Study engagement clock origin must be finite');
    }
    this.advancedAt = startedAt;
  }

  noteMaterialInteraction(now: number): void {
    this.noteInteraction('material', now);
  }

  noteTeachingInteraction(now: number): void {
    this.noteInteraction('teaching', now);
  }

  setDocumentVisible(visible: boolean, now: number): void {
    if (!this.advance(now)) return;
    this.documentVisible = visible;
  }

  setMaterialVisible(visible: boolean, now: number): void {
    if (!this.advance(now)) return;
    this.materialVisible = visible;
  }

  tick(now: number): EngagementSnapshot {
    this.advance(now);
    return this.snapshot();
  }

  private noteInteraction(surface: InteractiveSurface, now: number): void {
    if (!this.advance(now) || !this.isSurfaceEligible(surface)) return;
    this.owner = surface;
    this.ownerInteractionAt = now;
  }

  /** Returns false without mutation for an invalid monotonic timestamp. */
  private advance(now: number): boolean {
    if (!Number.isFinite(now) || now < this.advancedAt) return false;

    const elapsed = now - this.advancedAt;
    if (this.documentVisible && this.materialVisible) {
      this.materialVisibleMs += elapsed;
    }

    const activeSurface = this.activeSurfaceAt(this.advancedAt);
    if (activeSurface !== 'inactive' && this.ownerInteractionAt !== undefined) {
      const activeUntil = Math.min(now, this.ownerInteractionAt + INACTIVITY_CUTOFF_MS);
      const activeElapsed = Math.max(0, activeUntil - this.advancedAt);
      if (activeSurface === 'material') {
        this.materialActiveMs += activeElapsed;
      } else {
        this.teachingActiveMs += activeElapsed;
      }
    }

    this.advancedAt = now;
    return true;
  }

  private activeSurfaceAt(now: number): EngagementSurface {
    if (this.owner === undefined
      || this.ownerInteractionAt === undefined
      || now >= this.ownerInteractionAt + INACTIVITY_CUTOFF_MS
      || !this.isSurfaceEligible(this.owner)) {
      return 'inactive';
    }
    return this.owner;
  }

  private isSurfaceEligible(surface: InteractiveSurface): boolean {
    return this.documentVisible && (surface === 'teaching' || this.materialVisible);
  }

  private snapshot(): EngagementSnapshot {
    return {
      materialVisibleMs: this.materialVisibleMs,
      materialActiveMs: this.materialActiveMs,
      teachingActiveMs: this.teachingActiveMs,
      activeSurface: this.activeSurfaceAt(this.advancedAt),
    };
  }
}
